import React, { useContext } from 'react';
import { bool, func } from 'prop-types';
import { Box, Drawer, FormControl, Input, MenuItem, Select, useTheme } from '@mui/material';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as R from 'ramda';

// Context
import { AuthContext } from 'authentication/AuthContext';

// Utils
import { getCurrentTab, getMenuOptions, getRoute } from 'navigation/utils/nav';

// Components
import { ElseFlag, EvaluateFlag } from 'common/FeatureFlags/Flag';
import TopBar from './TopBarConnector';
import Footer from './Footer';
import RouteAccordion from './route-accordion/RouteAccordionConnector';

const shouldShow = item => {
  if (item?.feature) {
    // For supplier and carrier list-view flag: ON hides sub-links
    return !EvaluateFlag(item.feature);
  }
  if (item?.ldelse) {
    return ElseFlag(item.ldelse);
  }
  if (item?.ldfeature) {
    return EvaluateFlag(item.ldfeature);
  }

  return true;
};

const SideNavigationDrawer = ({ isNavDrawerOpen, setIsNavDrawerOpen }) => {
  const theme = useTheme();

  const { affiliateRoles, roles } = useContext(AuthContext);
  const combinedRoles = R.concat(affiliateRoles, roles);

  const { currentAffiliateId, dataCompanyId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const menuOptions = getMenuOptions(roles);
  const adjustedPath = pathname.split('/').slice(4).join('/');
  const currentTab = getCurrentTab(adjustedPath);

  // Filter menu options based on top-level feature flags
  const availableMenuOptions = menuOptions.filter(item => shouldShow(item));

  // Ensure currentTab.id exists in available options, otherwise use empty string
  const selectValue = availableMenuOptions.some(option => option.value === currentTab.id)
    ? currentTab.id
    : '';

  // controls when a user selects an option to change routes
  const handleDropdownChange = value => {
    const route = getRoute(value);
    const base = `/affiliate/${currentAffiliateId}/${dataCompanyId}`;
    // Home returns empty string and when empty we do not want to add a / to the end of the route
    // when navigating from Home the url is given the / at the end of base route
    const routeToPush = !R.isEmpty(route) ? `${base}/${route}` : base;
    navigate(routeToPush);
  };

  return (
    <Drawer
      anchor="left"
      onClose={() => setIsNavDrawerOpen(false)}
      open={isNavDrawerOpen}
      slotProps={{
        paper: {
          sx: {
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            backgroundColor: 'graphic.lighter',
            borderRight: 'none',
            boxShadow: 4,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            width: { xs: '100%', sm: 320 },
          },
        },
      }}
      variant="temporary"
    >
      <TopBar />
      <FormControl
        elevation={8}
        fullWidth
        sx={{ '& > .MuiFilledInput-root': { backgroundColor: 'transparent' } }}
      >
        <Select
          className="heap-menu"
          defaultValue=""
          IconComponent={ArrowDropDownRoundedIcon}
          id="nav-dropdown"
          input={
            <Input
              disableUnderline
              sx={{
                '&.MuiInputBase-root': {
                  '& > svg': {
                    fill: 'text.primary',
                    right: 16,
                    top: 28,
                  },
                  ...theme.typography.h6,
                  color: 'text.primary',
                  mt: 0,
                  pb: 0.5,
                  pt: 3,
                  px: 2,
                },
              }}
            />
          }
          inputProps={{
            sx: {
              '&.MuiFilledInput-root:before': {
                border: 'none',
              },
            },
          }}
          MenuProps={{
            anchorOrigin: {
              horizontal: 'left',
              vertical: 'top',
            },
          }}
          name="nav"
          onChange={e => handleDropdownChange(e.target.value)}
          value={selectValue}
        >
          {availableMenuOptions.map(item => (
            <MenuItem
              key={`${item.label}-${item.value}`}
              onClick={() => handleDropdownChange(item.value)}
              sx={{ p: `6px ${2}` }}
              value={item.value}
            >
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          px: 1,
          py: 0,
        }}
      >
        {currentTab?.subRoutes
          ?.filter(item => shouldShow(item) && shouldShow(currentTab))
          .map(({ id, userRoles, ...rest }) => {
            if (!userRoles(combinedRoles)) {
              return null;
            }

            const visibleSubRoutes = (rest.subRoutes || []).filter(shouldShow);

            return <RouteAccordion id={id} key={id} {...rest} subRoutes={visibleSubRoutes} />;
          })}
      </Box>
      <Footer />
    </Drawer>
  );
};

SideNavigationDrawer.propTypes = {
  isNavDrawerOpen: bool.isRequired,
  setIsNavDrawerOpen: func.isRequired,
};

export default SideNavigationDrawer;
